import { ReactNode, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { formatDate } from '../utils/utils'
import styles from '../candidates/CandidatesContainer.module.scss'

import { Assignee, Flex, Typography } from '@percent/lemonade'
import { useMutation, useQuery } from '@percent/partner-dashboard/common/hooks'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import { usePermissionCheck } from '@percent/partner-dashboard/common/hooks/useAllow/usePermissionCheck'
import { TaskInGetCandidates } from '@percent/partner-dashboard/services/grants/grantsService.types'

export function useTaskAssignees() {
  const { t } = useTranslation()
  const { grantsService } = useServices()
  const { userCan } = usePermissionCheck()
  const [searchValues, setSearchValues] = useState<Record<string, string>>({})
  const [assigneesMap, setAssigneesMap] = useState<Record<string, Assignee>>({})

  const [{ data: taskAssignees, isLoading: loadingTaskAssignees }] = useQuery(grantsService.getTaskAssignees, {})
  const [{ isLoading: isUpdatingTaskAssignee }, { apiFunc: updateTaskAssignee }] = useMutation(
    grantsService.updateTaskAssignee
  )

  const canAssignTasks = userCan('assignGrantsTasks')

  const handleAssigneeChange = (taskId: string, assignee: Assignee) => {
    updateTaskAssignee({
      taskId,
      assignedTo: assignee.id
    })
    setAssigneesMap(prev => ({ ...prev, [taskId]: assignee }))
  }

  const getFilteredAssignees = (taskTemplateId: string) =>
    taskAssignees?.filter(assignee =>
      assignee.email.toLowerCase().includes((searchValues[taskTemplateId] || '').toLowerCase())
    )

  const getAssignee = (taskId: string, taskAssignee?: { accountId: string; email: string }): Assignee | undefined =>
    assigneesMap[taskId] || (taskAssignee ? { id: taskAssignee.accountId, email: taskAssignee.email } : undefined)

  const getSearchProps = (taskTemplateId: string) => ({
    value: searchValues[taskTemplateId] || '',
    onChange: (value: string) => {
      setSearchValues(prev => ({ ...prev, [taskTemplateId]: value }))
    },
    placeholder: t('grants.assigneePicker.searchPlaceholder')
  })

  const getTooltipContent = useCallback(
    (task?: TaskInGetCandidates, assignee?: Assignee): ReactNode => {
      if (!assignee) {
        if (!task?.id) {
          return (
            <Flex className={styles.assigneePickerTooltip}>
              <Typography variant="small" $color="white">
                {t('grants.assigneePicker.tooltip.taskNotAvailable')}
              </Typography>
            </Flex>
          )
        }

        return null
      }

      return (
        <Flex className={styles.assigneePickerTooltip}>
          <Typography variant="small" $color="white">
            {t('grants.assigneePicker.tooltip.assignee', { assignee: assignee?.email })}
          </Typography>
          <Typography variant="small" $color="white">
            {t('grants.assigneePicker.tooltip.deadline', {
              deadline: task?.deadline ? formatDate(task.deadline) : t('typography.notAvailable')
            })}
          </Typography>
        </Flex>
      )
    },
    [t]
  )

  return {
    taskAssignees,
    loadingTaskAssignees,
    isUpdatingTaskAssignee,
    canAssignTasks,
    handleAssigneeChange,
    getFilteredAssignees,
    getAssignee,
    getSearchProps,
    getTooltipContent
  }
}
